import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OrganizationProvider, useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import Sidebar from '../common/sidebar';
import TopBar from '../common/topbar';
import GA4 from './GA4';
import GoogleSearchConsole from './GoogleSearchConsole';
import Members from './members';
import Billing from './Billing';
import { Toaster } from 'react-hot-toast';
import useGA4OAuthCallback from '../../../hooks/useGA4OAuthCallback';
import useGSCOAuthCallback from '../../../hooks/useGSCOAuthCallback';

const AccountPageContent = () => {
  const { organizationId } = useParams();
  const { organizationData, fetchOrganizationData, userRole } = useOrganization();
  const { userInfo } = useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("tools"); // Default to 'tools' tab
  const [activeToolSubTab, setActiveToolSubTab] = useState("ga4"); // Default tool is GA4
  const [loading, setLoading] = useState(true);

  // Handle GA4 OAuth callback notifications
  useGA4OAuthCallback();

  // Handle GSC OAuth callback notifications
  useGSCOAuthCallback();

  // Check if user is admin
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationData(organizationId);
      setLoading(false);
    }
  }, [organizationId, fetchOrganizationData]);

  // Function to switch tabs
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  if (!organizationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CC457D] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  const orgName = organizationData?.name || organizationData?.agency_name || organizationData?.organization_name || 'Unknown Organization';

  return (
    <div className="flex bg-dashboard-gradient h-screen font-inter overflow-hidden">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <TopBar />
        
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Header section - stays fixed */}
          <div className="p-6 pb-0 bg-white">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Account Settings</h2>
              <p className="text-sm text-gray-600">Manage account settings and preferences for {orgName}</p>
            </div>
          </div>

          {/* Tabs section - stays fixed */}
          <div className="px-6 border-b border-gray-200">
            <div className="flex">
              {/* Tools Tab */}
              <div
                className="px-5 py-2.5 cursor-pointer flex items-center gap-2 text-sm font-medium transition-colors"
                onClick={() => handleTabClick("tools")}
                style={{
                  borderBottom:
                    activeTab === "tools" ? "1px solid #404880" : "none",
                  color: activeTab === "tools" ? "#404880" : "#878787",
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Tools
              </div>

              {/* Members Tab */}
              <div
                className="px-5 py-2.5 cursor-pointer flex items-center gap-2 text-sm font-medium transition-colors"
                onClick={() => handleTabClick("members")}
                style={{
                  borderBottom:
                    activeTab === "members" ? "1px solid #404880" : "none",
                  color: activeTab === "members" ? "#404880" : "#878787",
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Members
              </div>

              {/* Billing Tab */}
              <div
                className="px-5 py-2.5 cursor-pointer flex items-center gap-2 text-sm font-medium transition-colors"
                onClick={() => handleTabClick("billing")}
                style={{
                  borderBottom:
                    activeTab === "billing" ? "1px solid #404880" : "none",
                  color: activeTab === "billing" ? "#404880" : "#878787",
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Billing
              </div>
            </div>

            {/* Sub-tabs for Tools */}
            {activeTab === "tools" && (
              <div className="flex border-t border-gray-100 px-6 py-3 gap-2">
                {/* Google Analytics 4 Sub-tab */}
                <div
                  className="px-4 py-1.5 cursor-pointer text-sm font-medium rounded-full transition-all"
                  onClick={() => setActiveToolSubTab("ga4")}
                  style={{
                    backgroundColor: activeToolSubTab === "ga4" ? "#F0F4F8" : "transparent",
                    color: activeToolSubTab === "ga4" ? "#404880" : "#A0AEC0",
                  }}
                >
                  Google Analytics 4
                </div>

                {/* Google Search Console Sub-tab */}
                <div
                  className="px-4 py-1.5 cursor-pointer text-sm font-medium rounded-full transition-all"
                  onClick={() => setActiveToolSubTab("gsc")}
                  style={{
                    backgroundColor: activeToolSubTab === "gsc" ? "#F0F4F8" : "transparent",
                    color: activeToolSubTab === "gsc" ? "#404880" : "#A0AEC0",
                  }}
                >
                  Google Search Console
                </div>
              </div>
            )}
          </div>

          {/* Content section - scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {!loading && (
              <>
                {activeTab === "tools" && activeToolSubTab === "ga4" && (
                  <GA4 
                    organizationData={organizationData} 
                    organizationId={organizationId}
                  />
                )}

                {activeTab === "tools" && activeToolSubTab === "gsc" && (
                  <GoogleSearchConsole
                    organizationData={organizationData}
                    organizationId={organizationId}
                  />
                )}
                
                {activeTab === "members" && (
                  <Members 
                    organizationData={organizationData} 
                    organizationId={organizationId} 
                  />
                )}

                {activeTab === "billing" && (
                  <Billing 
                    organizationId={organizationId} 
                  />
                )}
              </>
            )}
            
            {loading && (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
};

// Main component that provides the organization context
const AccountPage = () => {
  return (
    <OrganizationProvider>
      <AccountPageContent />
    </OrganizationProvider>
  );
};

export default AccountPage;
